import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { fetchData } from "../../utils/functions";
import { useNavigate, useParams } from "react-router-dom"

// Dynamic validation schema based on health status
const getValidationSchema = (healthStatus) => {
  if (healthStatus === "Non Functional") {
    // Only validate the notes if facility is non-functional
    return Yup.object().shape({
      issues_faced_by_patients: Yup.string()
        .trim()
        .required("Issues faced by patients is required"),
      suggestions_for_improvement: Yup.string()
        .trim()
        .required("Suggestions for improvement is required"),
    });
  }

  // Full validation if functional
  return Yup.object().shape({
    outpatient_services: Yup.boolean().required(
      "Outpatient Services is required"
    ),
    maternal_services: Yup.boolean().required("Maternal Services is required"),
    child_services: Yup.boolean().required("Child Services is required"),
    laboratory_services: Yup.boolean().required(
      "Laboratory Services is required"
    ),
    family_planning_services: Yup.boolean().required(
      "Family Planning Services is required"
    ),
    emergency_care: Yup.boolean().required("Emergency Care is required"),
    service_24_7: Yup.boolean().required("24/7 Service is required"),
    referral_system: Yup.boolean().required("Referral System is required"),
    issues_faced_by_patients: Yup.string()
      .trim()
      .required("Issues faced by patients is required"),
    suggestions_for_improvement: Yup.string()
      .trim()
      .required("Suggestions for improvement is required"),
  });
};

const EssentialServices = ({
  healthServices,
  healthFacilityId,
  isDqaApproved = false,
  onUpdate,
  healthStatus,
}) => {
  const [formData, setFormData] = useState({
    outpatient_services: null,
    maternal_services: null,
    child_services: null,
    laboratory_services: null,
    family_planning_services: null,
    emergency_care: null,
    service_24_7: null,
    referral_system: null,
    issues_faced_by_patients: "",
    suggestions_for_improvement: "",
  });

  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
    const { id } = useParams();
        const navigate = useNavigate();
         const [isDqaApproved2, setIsDqaApproved] = useState(false);
         const [isDqaRejected, setIsDqaRejected] = useState(false);
          const [isFetching, setIsFetching] = useState(false);
          const [loader, setLoader] = useState(false);

          const fetchHealthFacilityAssessment = async () => {
                    try {
                      setLoader(true);
                      setIsFetching(true);
                
                      const response = await fetchData(
                        `health-fetchById/${id}`,
                        "GET",
                        null,
                        {},
                        {},
                        navigate
                      );            
                
                      if (response?.data) {
                        // setSchoolData(response.data);
                        const isDqaApproved =
                          response.data?.healthFacility.verified_from_dqa === 1 ||
                          response.data?.healthFacility.verified_from_dqa === "1";

                          const isDqaRejected = 
                          response.data?.healthFacility.verified_from_dqa === 2 ||
                          response.data?.healthFacility.verified_from_dqa === "2";
                
                
                        setIsDqaApproved(isDqaApproved);
                        setIsDqaRejected(isDqaRejected)
                      }
                    } catch (error) {
                      console.error("Error fetching school data:", error);
                      toast.error("Failed to load school data.");
                    } finally {
                      setLoader(false);
                      setIsFetching(false);
                    }
                  };
                
                  useEffect(() => {
                    fetchHealthFacilityAssessment();
                  }, [id]);

  useEffect(() => {
    if (healthServices) {
      setFormData({
        outpatient_services: healthServices?.outpatient_services ?? null,
        maternal_services: healthServices?.maternal_services ?? null,
        child_services: healthServices?.child_services ?? null,
        laboratory_services: healthServices?.laboratory_services ?? null,
        family_planning_services:
          healthServices?.family_planning_services ?? null,
        emergency_care: healthServices?.emergency_care ?? null,
        service_24_7: healthServices?.service_24_7 ?? null,
        referral_system: healthServices?.referral_system ?? null,
        issues_faced_by_patients:
          healthServices?.issues_faced_by_patients || "",
        suggestions_for_improvement:
          healthServices?.suggestions_for_improvement || "",
      });
    }
  }, [healthServices]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    setErrors({});

    try {
      // Use dynamic validation based on health status
      const validationSchema = getValidationSchema(healthStatus);
      await validationSchema.validate(formData, { abortEarly: false });

      const updatePayload = {
        health_services: {
          outpatient_services: formData.outpatient_services,
          maternal_services: formData.maternal_services,
          child_services: formData.child_services,
          laboratory_services: formData.laboratory_services,
          family_planning_services: formData.family_planning_services,
          emergency_care: formData.emergency_care,
          service_24_7: formData.service_24_7,
          referral_system: formData.referral_system,
          issues_faced_by_patients: formData.issues_faced_by_patients,
          suggestions_for_improvement: formData.suggestions_for_improvement,
        },
      };

      const response = await fetchData(
        `health-facility-update/${healthServices.health_facility_id}`,
        "PUT",
        updatePayload,
        { "Content-Type": "application/json" }
      );

      if (response && response.success) {
        toast.success("Essential Services updated successfully!");
        if (onUpdate) onUpdate(updatePayload.health_services);
      } else {
        toast.error(response?.message || "Update failed!");
      }
    } catch (error) {
      if (error.inner) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      } else {
        console.error("Update error:", error);
        toast.error("Update failed");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const renderYesNo = (label, field, number) => (
    <div className="mb-4">
      <p className="mb-2 text-sm font-medium text-gray-700">
        {number}. {label}
      </p>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={formData[field] === true}
            onChange={() => handleChange(field, true)}
            disabled={isDqaApproved || isDqaRejected}
            className="form-radio text-teal-600"
          />
          Yes
        </label>
        <label className="flex items-center gap-1">
          <input
            type="radio"
            checked={formData[field] === false}
            onChange={() => handleChange(field, false)}
            disabled={isDqaApproved || isDqaRejected}
            className="form-radio text-teal-600"
          />
          No
        </label>
      </div>
      {errors[field] && <p className="text-red-500 text-xs">{errors[field]}</p>}
    </div>
  );

  // Check if functional (case-insensitive)
  const isFunctional = healthStatus?.toLowerCase() === "functional";

  return (
    <div>
      {/* Debug info - remove after testing */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="bg-blue-50 p-2 mb-4 text-xs">
          Debug: healthStatus = "{healthStatus}" | isFunctional = {isFunctional ? "true" : "false"}
        </div>
      )} */}
      <div className="flex justify-between items-center mb-6">
        {isDqaApproved2 && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Essential Services Availability
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Essential Services Availability
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>

      {/* Only show services if Functional */}
      {isFunctional && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderYesNo(
              "Outpatient Services (OPD)",
              "outpatient_services",
              12
            )}
            {renderYesNo(
              "Maternal Services (ANC, PNC, Delivery)",
              "maternal_services",
              13
            )}
            {renderYesNo(
              "Child Services (Immunization, Growth Monitoring)",
              "child_services",
              14
            )}
            {renderYesNo(
              "Laboratory (Testing services)",
              "laboratory_services",
              15
            )}
            {renderYesNo(
              "Family Planning Services",
              "family_planning_services",
              16
            )}
            {renderYesNo("Emergency Care / First Aid", "emergency_care", 17)}
            {renderYesNo("24/7 Service Availability", "service_24_7", 18)}
            {renderYesNo("Referral System in Place", "referral_system", 19)}
          </div>
        </>
      )}

      {/* Notes - Always visible */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
          Additional Notes
        </h2>

        <div className="grid grid-cols-1 gap-6">
          {/* Only show Issues Faced if Functional */}
          {healthStatus?.toLowerCase() === "functional" && (
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                1. Issues faced by patients
              </label>
              <textarea
                value={formData.issues_faced_by_patients}
                onChange={(e) =>
                  handleChange("issues_faced_by_patients", e.target.value)
                }
                disabled={isDqaApproved || isDqaRejected}
                className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                rows={3}
              />
              {errors.issues_faced_by_patients && (
                <p className="text-red-500 text-xs">
                  {errors.issues_faced_by_patients}
                </p>
              )}
            </div>
          )}

          {/* Always show Suggestions */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              {healthStatus?.toLowerCase() === "functional"
                ? "2. Suggestions for improvement"
                : "1. Suggestions for improvement"}
            </label>
            <textarea
              value={formData.suggestions_for_improvement}
              onChange={(e) =>
                handleChange("suggestions_for_improvement", e.target.value)
              }
              disabled={isDqaApproved || isDqaRejected}
              className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
              rows={3}
            />
            {errors.suggestions_for_improvement && (
              <p className="text-red-500 text-xs">
                {errors.suggestions_for_improvement}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Update Button */}
      {!isDqaApproved2 && !isDqaRejected ? (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className={`${
              isUpdating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
        </div>
      ) : (
        <div className=""></div>
      )}
    </div>
  );
};

export default EssentialServices;
